/**
 * Composable para gestión de URLs de imágenes desde buckets S3
 *
 * Soporta buckets públicos (assets) y privados (files).
 * Para buckets privados usa presigned URLs con cache del lado del cliente.
 */

import { IMAGE_TYPES, BUCKET_TYPES, CACHE_CONFIG } from '~/utils/image/imageConfig'
import {useFilesApi} from '~/composables/api/useFilesApi.js'

/**
 * Cache global compartido entre todas las instancias del composable
 * Estructura: Map<cacheKey, { url, expiresAt, timestamp }>
 */
const imageCache = new Map()

/**
 * Peticiones en vuelo para deduplicación
 * Estructura: Map<cacheKey, Promise>
 */
const pendingRequests = new Map()

/**
 * Genera la clave de cache para una imagen
 */
function getCacheKey(url, type) {
  return `${type}:${url}`
}

/**
 * Limpia entradas expiradas del cache
 */
function cleanExpiredEntries() {
  const now = Date.now()
  for (const [key, entry] of imageCache.entries()) {
    if (entry.expiresAt <= now) {
      imageCache.delete(key)
    }
  }
}

/**
 * Evicción LRU cuando el cache excede el tamaño máximo
 */
function evictLRU() {
  if (imageCache.size <= CACHE_CONFIG.maxEntries) return

  // Ordenar por timestamp y eliminar los más antiguos
  const entries = [...imageCache.entries()]
    .sort((a, b) => a[1].timestamp - b[1].timestamp)

  const toEvict = entries.slice(0, entries.length - CACHE_CONFIG.maxEntries)
  for (const [key] of toEvict) {
    imageCache.delete(key)
  }
}

/**
 * Composable para gestión de URLs de imágenes
 */
export const useImageUrl = () => {
  const configStore = useConfigStore()


  /**
   * Obtiene la URL base según el tipo de bucket
   */
  const getBaseUrl = (bucketType) => {
    if (bucketType === 'assets') {
      return configStore.assetsBaseUrl
    }
    if (bucketType === 'files') {
      return configStore.filesBaseUrl
    }
    return null
  }

  /**
   * Obtiene la URL de placeholder para un tipo de imagen
   * Los placeholders siempre se sirven desde el bucket 'assets' (público)
   * @param {string} type - Tipo de imagen (generic, promotion, user, product)
   * @returns {string|null} - URL del placeholder o null si no existe
   */
  const getPlaceholderUrl = (type) => {
    const imageType = IMAGE_TYPES[type] || IMAGE_TYPES.generic
    if (!imageType.placeholder) return null

    // Los placeholders siempre se sirven desde el bucket de assets (público)
    const placeholderBucket = imageType.placeholderBucket || 'assets'
    const baseUrl = getBaseUrl(placeholderBucket)
    if (!baseUrl) return null

    return `${baseUrl}/${imageType.placeholder}`
  }

  /**
   * Obtiene una presigned URL para un archivo en el bucket privado
   * @param {string} filePath - Ruta del archivo
   * @param {number} expiresIn - Tiempo de expiración en segundos
   * @returns {Promise<string>} - Presigned URL
   */
  const fetchPresignedUrl = async (filePath, expiresIn = CACHE_CONFIG.defaultTTL) => {
    try {
      const { getPresignedUrl } = useFilesApi()
      const response = await getPresignedUrl(filePath, { expiresIn })

      // La respuesta puede tener diferentes estructuras según el backend
      // Ajustar según la respuesta real del endpoint
      const url = response?.data?.url || response?.url || response
      return url
    } catch (error) {
      console.error('[useImageUrl] Error fetching presigned URL:', error)
      throw error
    }
  }

  /**
   * Obtiene la URL de una imagen (async)
   * - Para buckets públicos: retorna URL directa
   * - Para buckets privados: obtiene presigned URL con cache
   *
   * @param {string|null} imagePath - Ruta de la imagen (sin base URL)
   * @param {string} type - Tipo de imagen (generic, promotion, user, product, file)
   * @param {Object} options - Opciones adicionales
   * @param {number} options.ttl - TTL del cache en segundos (default: CACHE_CONFIG.defaultTTL)
   * @param {boolean} options.usePlaceholder - Usar placeholder si no hay imagen (default: true)
   * @returns {Promise<string|null>} - URL de la imagen o placeholder
   */
  const getImageUrl = async (imagePath, type = 'generic', options = {}) => {
    const { ttl = CACHE_CONFIG.defaultTTL, usePlaceholder = true } = options

    // Si no hay imagen, retornar placeholder
    if (!imagePath) {
      return usePlaceholder ? getPlaceholderUrl('assets') : null
    }

    const imageType = IMAGE_TYPES[type] || IMAGE_TYPES.generic
    const bucketAccessType = BUCKET_TYPES[imageType.bucket]

    // Para buckets públicos, construir URL directa
    if (bucketAccessType === 'public') {
      const baseUrl = getBaseUrl(imageType.bucket)
      if (!baseUrl) {
        return usePlaceholder ? getPlaceholderUrl(type) : null
      }
      return `${baseUrl}/${imagePath}`
    }

    // Para buckets privados, usar presigned URL con cache
    const cacheKey = getCacheKey(imagePath, type)

    // Limpiar entradas expiradas periódicamente
    cleanExpiredEntries()

    // Verificar cache
    const cached = imageCache.get(cacheKey)
    if (cached && cached.expiresAt > Date.now()) {
      return cached.url
    }

    // Deduplicación: si ya hay una petición en vuelo, esperarla
    const pending = pendingRequests.get(cacheKey)
    if (pending) {
      return pending
    }

    // Crear nueva petición
    const request = (async () => {
      try {
        const url = await fetchPresignedUrl(imagePath, ttl)

        // Guardar en cache
        imageCache.set(cacheKey, {
          url,
          expiresAt: Date.now() + (ttl * 1000),
          timestamp: Date.now()
        })

        // Evicción LRU si es necesario
        evictLRU()

        return url
      } catch (error) {
        console.warn(`[useImageUrl] Error fetching presigned URL for ${imagePath}:`, error)

        // En caso de error, retornar placeholder si está disponible
        if (usePlaceholder) {
          return getPlaceholderUrl(type)
        }
        return null
      } finally {
        // Limpiar petición pendiente
        pendingRequests.delete(cacheKey)
      }
    })()

    pendingRequests.set(cacheKey, request)
    return request
  }

  /**
   * Obtiene la URL de una imagen de forma síncrona
   * Solo funciona para:
   * - Placeholders (sin imagen)
   * - Imágenes en buckets públicos (si hay config cargada)
   * - URLs cacheadas que no han expirado
   *
   * @param {string|null} imagePath - Ruta de la imagen
   * @param {string} type - Tipo de imagen
   * @returns {string|null} - URL de la imagen o null si requiere petición async
   */
  const getImageUrlSync = (imagePath, type = 'generic') => {
    // Si no hay imagen, retornar placeholder
    if (!imagePath) {
      return getPlaceholderUrl(type)
    }

    const imageType = IMAGE_TYPES[type] || IMAGE_TYPES.generic
    const bucketAccessType = BUCKET_TYPES[imageType.bucket]

    // Para buckets públicos, construir URL directa
    if (bucketAccessType === 'public') {
      const baseUrl = getBaseUrl(imageType.bucket)
      if (!baseUrl) return null
      return `${baseUrl}/${imagePath}`
    }

    // Para buckets privados, verificar si está en cache
    const cacheKey = getCacheKey(imagePath, type)
    const cached = imageCache.get(cacheKey)

    if (cached && cached.expiresAt > Date.now()) {
      return cached.url
    }

    // No se puede obtener síncronamente
    return null
  }

  /**
   * Precarga múltiples imágenes en paralelo
   * Útil para cargar imágenes de una lista antes de mostrarlas
   *
   * @param {Array<{url: string, type: string}>} images - Array de imágenes a precargar
   * @returns {Promise<void>}
   */
  const preloadImages = async (images) => {
    if (!images || !images.length) return

    const promises = images.map(({ url, type }) =>
      getImageUrl(url, type).catch(() => null) // Ignorar errores individuales
    )

    await Promise.all(promises)
  }

  /**
   * Limpia todo el cache de imágenes
   * Útil cuando se detecta que las credenciales han cambiado
   */
  const clearCache = () => {
    imageCache.clear()
    pendingRequests.clear()
  }

  /**
   * Invalida una entrada específica del cache
   * @param {string} imagePath - Ruta de la imagen
   * @param {string} type - Tipo de imagen
   */
  const invalidateCache = (imagePath, type = 'generic') => {
    const cacheKey = getCacheKey(imagePath, type)
    imageCache.delete(cacheKey)
    pendingRequests.delete(cacheKey)
  }

  return {
    getImageUrl,
    getImageUrlSync,
    getPlaceholderUrl,
    preloadImages,
    clearCache,
    invalidateCache
  }
}
